import {
  getCustomCssState,
  getCustomCssText,
  initCustomCssSelectionTracking,
  resolveEditor,
  setCustomCssOnEditorTarget,
} from './customCss';

/**
 * Custom Style Manager field for per-element CSS declarations (General sector).
 */
export function registerCustomCssProperty(editor) {
  const sm = editor?.StyleManager;
  if (!sm || typeof sm.addType !== 'function') return;

  console.warn('[custom-css] registerCustomCssProperty');

  initCustomCssSelectionTracking(editor);

  const typeId = 'custom-css-editor';

  // Always (re)register — GrapesJS property types are shared across editor
  // instances, so skipping when getType() exists leaves a stale editor closure.
  sm.addType(typeId, {
    create() {
      const em = this.em;
      const getEditor = () => resolveEditor(em);

      console.warn('[custom-css] field create()', {
        emCid: em?.cid,
        hasEditor: !!getEditor(),
      });

      const wrap = document.createElement('div');
      wrap.className = 'gjs-field-custom-css';
      wrap.setAttribute('data-custom-css-field', '1');

      const hint = document.createElement('div');
      hint.className = 'gjs-custom-css-hint';
      hint.textContent = 'Applied on the selected element. Use Apply or Ctrl+Enter.';

      const textarea = document.createElement('textarea');
      textarea.className = 'gjs-custom-css-textarea';
      textarea.rows = 5;
      textarea.spellcheck = false;
      textarea.placeholder =
        'e.g.\nborder-radius: 8px;\nbox-shadow: 0 2px 8px rgba(0,0,0,0.15);';

      const actions = document.createElement('div');
      actions.className = 'gjs-custom-css-actions';

      const applyBtn = document.createElement('button');
      applyBtn.type = 'button';
      applyBtn.className = 'gjs-custom-css-apply-btn';
      applyBtn.textContent = 'Apply';
      applyBtn.setAttribute('data-custom-css-apply', '1');

      let editing = false;
      let appliedTimer = null;

      const resetHint = () => {
        hint.textContent = 'Applied on the selected element. Use Apply or Ctrl+Enter.';
        hint.classList.remove('gjs-custom-css-hint--applied', 'gjs-custom-css-hint--error');
      };

      const showApplied = () => {
        hint.textContent = 'Applied.';
        hint.classList.add('gjs-custom-css-hint--applied');
        hint.classList.remove('gjs-custom-css-hint--error');
        if (appliedTimer) clearTimeout(appliedTimer);
        appliedTimer = setTimeout(resetHint, 1500);
      };

      const showError = (message) => {
        hint.textContent = message;
        hint.classList.add('gjs-custom-css-hint--error');
        hint.classList.remove('gjs-custom-css-hint--applied');
        if (appliedTimer) clearTimeout(appliedTimer);
        appliedTimer = setTimeout(resetHint, 2500);
      };

      const refreshFromSelection = () => {
        if (editing) return;
        const state = getCustomCssState(getEditor());
        textarea.value = getCustomCssText(state?.component || null);
      };

      const apply = (source = 'unknown') => {
        editing = false;
        const ed = getEditor();
        const state = getCustomCssState(ed);

        console.warn(`[custom-css] apply (${source})`, {
          cssText: textarea.value,
          hasEditor: !!ed,
          trackedComponent: state?.component?.cid,
          trackedTarget: state?.styleTarget?.cid,
        });

        const applied = setCustomCssOnEditorTarget(ed, textarea.value);
        console.warn('[custom-css] apply result:', applied);

        if (!applied) {
          showError('Select an element on the canvas first.');
          return;
        }
        showApplied();
      };

      textarea.addEventListener('focus', () => {
        editing = true;
      });

      textarea.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          e.stopPropagation();
          apply('ctrl+enter');
        }
      });

      wrap.addEventListener(
        'click',
        (e) => {
          const btn = e.target.closest('[data-custom-css-apply]');
          if (!btn) return;
          console.warn('[custom-css] apply click (delegated)');
          e.preventDefault();
          e.stopPropagation();
          apply('button-delegated');
        },
        true
      );

      applyBtn.addEventListener('click', (e) => {
        console.warn('[custom-css] apply click (button)');
        e.preventDefault();
        e.stopPropagation();
        apply('button');
      });

      const onSelected = () => refreshFromSelection();
      em.on('component:selected', onSelected);

      wrap._customCssTextarea = textarea;
      wrap._customCssEditing = () => editing;
      wrap._customCssDestroy = () => {
        em.off('component:selected', onSelected);
        if (appliedTimer) clearTimeout(appliedTimer);
      };

      actions.appendChild(applyBtn);
      wrap.appendChild(hint);
      wrap.appendChild(textarea);
      wrap.appendChild(actions);

      return wrap;
    },
    emit() {
      // Applied directly in create().
    },
    update({ el }) {
      if (el._customCssEditing && el._customCssEditing()) return;
      const textarea = el._customCssTextarea || el.querySelector('textarea');
      if (!textarea) return;
      const state = getCustomCssState(resolveEditor(this.em));
      textarea.value = getCustomCssText(state?.component || null);
    },
    destroy({ el }) {
      if (el?._customCssDestroy) el._customCssDestroy();
    },
  });
}
